import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Schedule,
  HourglassEmpty,
  CalendarToday,
  Visibility
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getAllProjects, resolveImageUrl } from '../utils/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
  },
}));

const StyledCardMedia = styled(CardMedia)({
  height: 250,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

// Projects Hero Section (similar to Home.js)
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  color: 'white',
  overflow: 'hidden',
  marginTop: '-90px',
  paddingTop: '90px',
  [theme.breakpoints.down('md')]: {
    minHeight: '80vh',
    paddingTop: '70px',
    marginTop: '-70px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '60vh',
    paddingTop: '56px',
    marginTop: '-56px',
  },
}));

const SliderContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
});

const SlideImage = styled(motion.div)(({ isActive }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: isActive ? 1 : 0,
  zIndex: 1,
  filter: 'brightness(1.1)',
  transition: 'opacity 0.6s cubic-bezier(0.4,0,0.2,1)',
  pointerEvents: 'none',
}));

const SliderOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.2)',
  zIndex: 2,
});

const SliderDots = styled(Box)({
  position: 'absolute',
  bottom: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '0.5rem',
  zIndex: 4,
});

const SliderDot = styled(Box)(({ isActive }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: isActive ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'white',
    transform: 'scale(1.2)',
  },
}));

const SlideContent = styled(motion.div)(({ isActive, theme }) => ({
  opacity: isActive ? 1 : 0,
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
  pointerEvents: isActive ? 'auto' : 'none',
  zIndex: 3,
  paddingLeft: '6vw',
  color: 'white',
  [theme.breakpoints.down('md')]: {
    paddingLeft: '3vw',
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '2vw',
    paddingTop: '4vh'
  },
}));

const Projects = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === '0000-00-00' || dateString === null) {
      return null; // Return null for missing dates
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null; // Return null for invalid dates
    }

    return date.toLocaleDateString();
  };

  // Helper functions for status display
  const getProjectStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return 'warning';
      case 'pending':
      case 'planning':
      case 'upcoming': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProjectStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return <Schedule />;
      case 'pending':
      case 'planning':
      case 'upcoming': return <HourglassEmpty />;
      default: return <HourglassEmpty />;
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!projects || projects.length === 0) return;
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.min(4, projects.length));
    }, 4000);
    return () => clearTimeout(timer);
  }, [currentSlide, projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getAllProjects();

      // Handle the actual API response structure
      const projectsData = response?.data?.projects || response?.projects || [];
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (err) {
      setError('Failed to load projects. Please try again later.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading projects...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Sort projects by completion_date (latest first), fallback to id if no date
  const sortedProjects = [...projects].sort((a, b) => {
    if (a.completion_date && b.completion_date) {
      return new Date(b.completion_date) - new Date(a.completion_date);
    }
    return (b.id || 0) - (a.id || 0);
  });
  const sliderProjects = sortedProjects.slice(0, 4);

  return (
    <>
      {/* Projects Hero Section with Slider and Header */}
      <HeroSection>
        <SliderContainer>
          {sliderProjects.map((project, idx) => (
            <SlideImage
              key={project.id}
              isActive={currentSlide === idx}
              style={{ backgroundImage: `url(${resolveImageUrl(project.image_url)})` }}
              animate={{ opacity: currentSlide === idx ? 1 : 0 }}
              transition={{ duration: 0.6 }}
            >
            </SlideImage>
          ))}
          <SliderOverlay />
          {sliderProjects.map((project, idx) => (
            <SlideContent key={project.id + '-content'} isActive={currentSlide === idx}>
              <Box>
                {/* Title split and color safely using theme */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.7rem', md: '2.2rem', lg: '2.7rem' },
                    lineHeight: 1.1,
                    maxWidth: { xs: '90vw', sm: '70vw', md: '500px' },
                  }}
                >
                  {/* First two words */}
                  {project.title.split(' ').slice(0, 2).join(' ')}{' '}
                  {/* Remaining words with color, only if present */}
                  {project.title.split(' ').length > 2 && (
                    <span style={{ color: theme ? theme.palette.secondary.main : '#1976d2' }}>
                      {project.title.split(' ').slice(2).join(' ')}
                    </span>
                  )}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '0.8rem', md: '1.1rem' },
                    fontWeight: 400,
                    opacity: 1,
                    maxWidth: { xs: '300px', sm: '400px', md: '500px' },
                    lineHeight: 1.6,
                  }}
                >
                  {project.description?.slice(0, 120)}{project.description?.length > 120 ? '...' : ''}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<Visibility />}
                  onClick={() => navigate(`/projects/${project.slug || project.id}`)}
                  sx={{
                    mt: 2,
                    py: 1,
                    px: 2.5,
                    fontSize: { xs: '0.5rem', sm: '0.7rem', md: '0.8rem', lg: '0.85rem', xl: '0.95rem' },
                    borderRadius: '50px',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    fontWeight: 600,
                    background: 'rgba(255,255,255,0.04)',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  View Project
                </Button>
              </Box>
            </SlideContent>
          ))}
          <SliderDots>
            {sliderProjects.map((_, idx) => (
              <SliderDot
                key={idx}
                isActive={currentSlide === idx}
                onClick={() => setCurrentSlide(idx)}
              />
            ))}
          </SliderDots>
        </SliderContainer>
      </HeroSection>

      {/* Header Section beneath Hero/Slider */}
      <Box sx={{ textAlign: 'center', mt: { xs: 2, md: 4 }, width: '100%' }}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: '#222',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            textShadow: '0 2px 16px rgba(0,0,0,0.08)',
          }}
        >
          Our Projects
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: '600px', mx: 'auto', textShadow: '0 2px 8px rgba(0,0,0,0.04)', fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' } }}
        >
          Explore our portfolio of successful construction projects. From residential
          buildings to commercial complexes, we deliver excellence in every project.
        </Typography>
      </Box>

      {/* Projects Grid Section */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        {projects.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No projects available at the moment.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <StyledCard>
                  <StyledCardMedia
                    image={resolveImageUrl(project.image_url)}
                    title={project.title}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {project.title}
                    </Typography>

                    {/* Status, Category and Completion Date - above description */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getProjectStatusIcon(project.status)}
                        <Chip
                          label={project.status || 'Unknown'}
                          color={getProjectStatusColor(project.status)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        {project.status?.toLowerCase() === 'completed' && formatDate(project.completion_date) ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday sx={{ fontSize: 14 }} />
                            Completed: {formatDate(project.completion_date)}
                          </Typography>
                        ) : project.status?.toLowerCase() === 'completed' ? (
                          <Typography variant="caption" color="text.secondary">
                            Completion Date : N/A
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {project.status?.toLowerCase() === 'ongoing' ? 'Project in progress' :
                              project.status?.toLowerCase() === 'upcoming' ? 'Project upcoming' :
                                'Status: ' + (project.status || 'Unknown')}
                          </Typography>
                        )}
                      </Box>
                      {project.category && (
                        <Chip
                          label={project.category}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {project.description}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/projects/${project.slug || project.id}`)}
                    >
                      View Details
                    </Button>
                    {project.images && project.images.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {project.images.length} image{project.images.length !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </>
  );
};

export default Projects;